import { useEffect, useMemo, useState } from "react";
import { invokeAdmin } from "@/pages/backstage/lib/adminApi";
import { holidayReason, upcomingBalineseHolidays } from "@/lib/balineseHolidays";

interface BalineseHolidayPickerProps {
  pin: string | null;
  isDe: boolean;
  blockedDates: string[];
  onSaved: () => void;
}

/** Verschiebt ein ISO-Datum um eine Anzahl Tage. */
function shiftIsoDate(iso: string, days: number): string {
  const parts = iso.split("-").map(Number);
  if (parts.length < 3 || parts.some((n) => !Number.isFinite(n))) return iso;
  const d = new Date(Date.UTC(parts[0], parts[1] - 1, parts[2] + days));
  return d.toISOString().slice(0, 10);
}

export default function BalineseHolidayPicker({
  pin,
  isDe,
  blockedDates,
  onSaved,
}: BalineseHolidayPickerProps) {
  const today = new Date().toISOString().split("T")[0];
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [withBuffer, setWithBuffer] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");

  const holidays = useMemo(() => upcomingBalineseHolidays(today), [today]);
  const blockedSet = useMemo(() => new Set(blockedDates), [blockedDates]);

  const openHolidays = useMemo(
    () => holidays.filter((h) => !blockedSet.has(h.date)),
    [holidays, blockedSet]
  );

  useEffect(() => {
    setSelected((prev) => {
      const next = new Set<string>();
      prev.forEach((d) => {
        if (!blockedSet.has(d)) next.add(d);
      });
      return next;
    });
  }, [blockedSet]);

  useEffect(() => {
    setWithBuffer((prev) => {
      const next = new Set<string>();
      prev.forEach((d) => {
        if (selected.has(d) && !blockedSet.has(d)) next.add(d);
      });
      return next;
    });
  }, [selected, blockedSet]);

  const grouped = useMemo(() => {
    const map = new Map<string, typeof holidays>();
    holidays.forEach((h) => {
      const year = h.date.slice(0, 4);
      const list = map.get(year) ?? [];
      list.push(h);
      map.set(year, list);
    });
    return Array.from(map.entries());
  }, [holidays]);

  /** Alle Tage, die beim Klick auf "Sperren" tatsächlich gesetzt werden. */
  const plannedDays = useMemo(() => {
    const map = new Map<string, string>();
    holidays.forEach((h) => {
      if (!selected.has(h.date)) return;
      const base = holidayReason(h);
      if (!blockedSet.has(h.date)) map.set(h.date, base);
      if (!withBuffer.has(h.date)) return;
      const around = [
        { date: shiftIsoDate(h.date, -1), suffixDe: "Vortag", suffixEn: "day before" },
        { date: shiftIsoDate(h.date, 1), suffixDe: "Folgetag", suffixEn: "day after" },
      ];
      around.forEach((entry) => {
        if (blockedSet.has(entry.date)) return;
        if (map.has(entry.date)) return;
        map.set(entry.date, `${base} (${isDe ? entry.suffixDe : entry.suffixEn})`);
      });
    });
    return Array.from(map.entries()).map(([date, reason]) => ({ date, reason }));
  }, [holidays, selected, withBuffer, blockedSet, isDe]);

  const formatDate = (iso: string) => {
    const parts = iso.split("-").map(Number);
    const d = new Date(parts[0], parts[1] - 1, parts[2]);
    return d.toLocaleDateString(isDe ? "de-DE" : "en-GB", {
      weekday: "short",
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  };

  const formatShort = (iso: string): string => {
    const parts = iso.split("-");
    if (parts.length < 3) return iso;
    return `${parts[2]}.${parts[1]}.`;
  };

  const toggle = (date: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(date)) next.delete(date);
      else next.add(date);
      return next;
    });
  };

  const toggleBuffer = (date: string) => {
    setWithBuffer((prev) => {
      const next = new Set(prev);
      if (next.has(date)) next.delete(date);
      else next.add(date);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size > 0) {
      setSelected(new Set<string>());
      setWithBuffer(new Set<string>());
      return;
    }
    setSelected(new Set(openHolidays.map((h) => h.date)));
  };

  const handleSave = async () => {
    if (!pin || plannedDays.length === 0) return;
    setSaving(true);
    setError("");
    setNotice("");
    try {
      const { error: err } = await invokeAdmin<{ ok?: boolean }>({
        action: "block_tour_days",
        pin,
        days: plannedDays,
      });
      if (err) throw new Error(err);
      setNotice(
        isDe
          ? `${plannedDays.length} Tag${plannedDays.length === 1 ? "" : "e"} als Sperrliste übernommen.`
          : `${plannedDays.length} day${plannedDays.length === 1 ? "" : "s"} added to the block list.`
      );
      setSelected(new Set());
      setWithBuffer(new Set());
      onSaved();
    } catch {
      setError(isDe ? "Übernehmen fehlgeschlagen." : "Applying failed.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-background-50 border border-background-200 rounded-lg p-5 mb-8">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <h3 className="font-heading text-xl text-foreground-950">
            {isDe ? "Balinesische Feiertage" : "Balinese holidays"}
          </h3>
          <p className="text-sm text-foreground-600 mt-1 max-w-xl">
            {isDe
              ? "Nyepi, Galungan und Kuningan sind beweglich. Auswahl mit einem Klick als Sperrliste übernehmen – die Tage werden in allen Tour-Formularen ausgegraut."
              : "Nyepi, Galungan and Kuningan move every year. Add your selection to the block list with one click; the days are greyed out in all tour forms."}
          </p>
        </div>
        <button
          type="button"
          onClick={toggleAll}
          className="inline-flex items-center gap-1.5 text-xs font-medium text-secondary-800 bg-secondary-100 hover:bg-secondary-200 border border-secondary-200 px-3 py-1.5 rounded-md whitespace-nowrap cursor-pointer transition-colors"
        >
          <i className={selected.size > 0 ? "ri-close-line" : "ri-check-double-line"}></i>
          {selected.size > 0
            ? isDe
              ? "Auswahl aufheben"
              : "Clear selection"
            : isDe
            ? "Alle auswählen"
            : "Select all"}
        </button>
      </div>

      {(notice || error) && (
        <div
          className={`mb-4 text-sm font-medium rounded-md px-4 py-2.5 border ${
            error
              ? "text-secondary-700 bg-secondary-100 border-secondary-200"
              : "text-primary-700 bg-primary-100 border-primary-200"
          }`}
        >
          {error || notice}
        </div>
      )}

      <div className="max-h-[360px] overflow-y-auto pr-1 space-y-4">
        {grouped.map(([year, list]) => (
          <div key={year}>
            <div className="text-[11px] uppercase tracking-wider text-foreground-500 mb-2">{year}</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {list.map((h) => {
                const isBlocked = blockedSet.has(h.date);
                const isSelected = selected.has(h.date);
                const bufferOn = withBuffer.has(h.date);
                const beforeIso = shiftIsoDate(h.date, -1);
                const afterIso = shiftIsoDate(h.date, 1);
                return (
                  <div
                    key={`${h.date}-${h.kind}`}
                    className={`rounded-md border transition-colors ${
                      isBlocked
                        ? "bg-background-100 border-background-200"
                        : isSelected
                        ? "bg-accent-100 border-accent-300"
                        : "bg-background-50 border-background-200 hover:bg-background-100"
                    }`}
                  >
                    <button
                      type="button"
                      disabled={isBlocked}
                      onClick={() => toggle(h.date)}
                      className={`flex items-start gap-2.5 text-left w-full px-3 py-2.5 ${
                        isBlocked ? "cursor-not-allowed" : "cursor-pointer"
                      }`}
                    >
                      <span
                        className={`mt-0.5 w-4 h-4 shrink-0 flex items-center justify-center rounded border ${
                          isBlocked
                            ? "bg-primary-600 border-primary-600 text-background-50"
                            : isSelected
                            ? "bg-accent-500 border-accent-500 text-background-50"
                            : "border-background-300 bg-background-50"
                        }`}
                      >
                        {(isBlocked || isSelected) && <i className="ri-check-line text-[11px]"></i>}
                      </span>
                      <span className="min-w-0">
                        <span
                          className={`block text-sm font-medium ${
                            isBlocked ? "text-foreground-400" : "text-foreground-900"
                          }`}
                        >
                          {formatDate(h.date)} · {isDe ? h.name : h.nameEn}
                        </span>
                        <span className="block text-xs text-foreground-500 mt-0.5">
                          {isBlocked
                            ? isDe
                              ? "bereits gesperrt"
                              : "already blocked"
                            : isDe
                            ? h.noteDe
                            : h.noteEn}
                        </span>
                      </span>
                    </button>

                    {isSelected && !isBlocked && (
                      <div className="px-3 pb-2.5">
                        <button
                          type="button"
                          onClick={() => toggleBuffer(h.date)}
                          title={
                            isDe
                              ? `Auch ${formatShort(beforeIso)} und ${formatShort(afterIso)} sperren`
                              : `Also block ${formatShort(beforeIso)} and ${formatShort(afterIso)}`
                          }
                          className={`inline-flex items-center gap-1.5 text-[11px] font-medium px-2 py-1 rounded-md border whitespace-nowrap cursor-pointer transition-colors ${
                            bufferOn
                              ? "bg-accent-500 border-accent-500 text-background-50"
                              : "bg-background-50 border-background-300 text-foreground-700 hover:bg-background-100"
                          }`}
                        >
                          <i
                            className={
                              bufferOn ? "ri-checkbox-circle-fill text-[13px]" : "ri-add-circle-line text-[13px]"
                            }
                          ></i>
                          {isDe ? "Vortag + Folgetag" : "Day before + after"}
                          <span className={bufferOn ? "opacity-90" : "text-foreground-500"}>
                            {formatShort(beforeIso)} / {formatShort(afterIso)}
                          </span>
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 mt-4 pt-4 border-t border-background-200">
        <p className="text-xs text-foreground-500 max-w-md">
          {isDe
            ? "Optional pro Feiertag „Vortag + Folgetag“ dazuschalten – die Tage bleiben einzeln in der Sperrliste und lassen sich dort jederzeit freigeben. Daten nach balinesischem Kalender (2026–2030), bitte vor jeder Saison kurz prüfen."
            : "Optionally switch on \"day before + after\" per holiday – those days stay as separate entries in the block list and can be released there at any time. Dates follow the Balinese calendar (2026–2030), please double-check before each season."}
        </p>
        <button
          type="button"
          onClick={handleSave}
          disabled={saving || plannedDays.length === 0}
          className="inline-flex items-center justify-center gap-2 bg-accent-500 hover:bg-accent-600 disabled:opacity-50 disabled:cursor-not-allowed text-background-50 text-sm font-medium px-5 py-2.5 rounded-md whitespace-nowrap cursor-pointer transition-colors"
        >
          {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-calendar-close-line"></i>}
          {isDe
            ? `Auswahl sperren${plannedDays.length > 0 ? ` (${plannedDays.length})` : ""}`
            : `Block selection${plannedDays.length > 0 ? ` (${plannedDays.length})` : ""}`}
        </button>
      </div>
    </div>
  );
}